/**
 * Services screen (DNKADM-0055 U1; grouped view DNKADM-0079) — the `v2_service`
 * entity list + create/edit/deactivate/reactivate + the DNKADM-0140 lifecycle actions
 * (Publish a Draft live, Copy/duplicate any service into a new Draft; the list shows
 * the three-state Draft/Active/Inactive status chip). Renders as the
 * default **Services** tab of the consolidated Services destination
 * ({@link ServicesCatalogScreen}); the sibling **Service categories** tab holds the
 * category CRUD. The catalog that drives availability and booking.
 *
 * Loads `listServices({ status })` through `useResource`, keyed on the Active/Inactive/
 * All filter (`useActiveFilter` + `<ActiveFilter>`, §4.3). The filter + "Add service"
 * button stay at the top in both views, joined by the DNKADM-0283 "Preview guest
 * booking" affordance ({@link GuestBookingPreviewDialog}) when the member origin is
 * recoverable from this console's host.
 *
 * **Grouped by category (DNKADM-0079; single table DNKADM-0089):** when the service
 * rows carry the inline `categories` summary (`HANDOFF-dink-api-service-row-categories`),
 * the rows render in ONE table — a single `<TableHead>` (the 8 columns) at the top, then
 * a `<TableBody>` where each category is a full-width grouping row (a chevron toggle +
 * name + service count spanning every column) followed, when open, by that category's
 * service rows in the SAME columns. The header renders ONCE and every column is equal
 * width across categories. The FIRST section is expanded by default. Sections are
 * sourced from the row summary + the full categories list (so an empty category still
 * shows). A multi-category service appears under EACH of its sections; a service tagged
 * with no category falls into a trailing "Uncategorized" section. Grouping rides on the
 * single services list call (+ the one categories list call) — there is NO per-service
 * `category-assignments` fan-out. **Forward-compatible:** until the field ships (it is
 * absent on every row), the screen renders today's FLAT table (absent ≠ empty — never
 * everything-in-Uncategorized). Both views render the same shared {@link ServiceRow}
 * (one row definition; the flat fallback keeps its own {@link ServicesTable} wrapper).
 *
 * Every write refetches + toasts. A first-run empty tenant (§8) shows a strong empty
 * state with a create CTA.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a
 * service name search and the Active/Inactive/All filter; every data column has a shared
 * sortable header (default Name ascending). The rows are searched and sorted BEFORE
 * grouping, so in the grouped view the sort applies within each category section and a
 * multi-category service is sorted into each of its sections; the sections keep their
 * order. While a search is active, a section with no match is hidden and the rest open. Search,
 * filter and sort live in the URL (`servicesListView.ts`).
 */
import { Fragment, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon, resolveMaps, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import { useUrlActiveFilter } from '@/hooks/useActiveFilter';
import { ActiveFilter } from '@/components/ActiveFilter';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import {
  copyService,
  deactivateService,
  listServices,
  publishService,
  reactivateService,
  type V2Service,
} from '@/api/services';
import { listServiceCategories } from '@/api/serviceCategories';
import { listCreditBuckets, type CreditBucket } from '@/api/creditBuckets';
import { ServiceDialog } from './ServiceDialog';
import { GuestBookingPreviewDialog } from './GuestBookingPreviewDialog';
import { guestBookingPreviewUrl } from './guestBookingPreview';
import {
  SERVICE_COLUMN_COUNT,
  ServiceRow,
  ServicesTable,
  ServicesTableHead,
} from './ServicesTable';
import {
  SERVICE_DEFAULT_SORT,
  SERVICE_SORT_KEYS,
  SERVICES_URL_DEFAULTS,
  serviceMatches,
  serviceSortValue,
  type ServiceSortKey,
} from './servicesListView';
import { ScreenError } from '@/components/ScreenError';

/** Synthetic id for the trailing "Uncategorized" section (no real category id). */
const UNCATEGORIZED_ID = '__uncategorized__';

/** One per-category section of the grouped Services view. */
interface CategorySection {
  id: string;
  name: string;
  services: V2Service[];
}

/**
 * The grouped layout, or `null` for the flat fallback. Grouping is built ONLY when
 * the inline `categories` field is present on at least one LOADED row (absent on every
 * row ⇒ `null` ⇒ the flat table; absent ≠ empty). The caller decides that from the whole
 * loaded list, not the searched rows, so a search with no match keeps the grouped view
 * (DNKADM-0430). Sections keep the categories-list order, then any category referenced
 * by a service but not in the list (resolved by its inline name); services with no
 * category collect in `uncategorized`.
 */
function groupByCategory(
  services: V2Service[],
  categories: { id: string; name: string }[],
  fieldPresent: boolean,
): { sections: CategorySection[]; uncategorized: V2Service[] } | null {
  if (!fieldPresent) return null;

  // Names resolve from the loaded categories list first (authoritative — reflects a
  // rename); an unlisted referenced id falls back to its inline name, then the id.
  const nameById = new Map<string, string>();
  const byId = new Map<string, V2Service[]>();
  const order: string[] = [];
  for (const c of categories) {
    const name = c.name.length > 0 ? c.name : c.id;
    nameById.set(c.id, name);
    byId.set(c.id, []);
    order.push(c.id);
  }

  const uncategorized: V2Service[] = [];
  for (const svc of services) {
    // A row without the field in grouped mode (shouldn't occur — the field is
    // all-or-none) is treated as no-category rather than dropped.
    const tags = svc.categories ?? [];
    if (tags.length === 0) {
      uncategorized.push(svc);
      continue;
    }
    for (const tag of tags) {
      if (!byId.has(tag.id)) {
        byId.set(tag.id, []);
        order.push(tag.id);
      }
      if (!nameById.has(tag.id)) nameById.set(tag.id, tag.name);
      byId.get(tag.id)!.push(svc);
    }
  }

  const sections = order.map((id) => ({
    id,
    name: nameById.get(id) ?? id,
    services: byId.get(id) ?? [],
  }));
  return { sections, uncategorized };
}

export function ServicesScreen() {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const maps = resolveMaps(theme);
  const showToast = useToast();
  // DNKADM-0430: search, status filter and sort come from the URL (`servicesListView.ts`).
  const url = useListUrlState(SERVICES_URL_DEFAULTS);
  const setUrl = url.set;
  const filter = useUrlActiveFilter(url.values.status, setUrl);
  const search = url.values.q;
  const searching = search.trim().length > 0;
  const sort = parseSort(url.values.sort, url.values.dir, SERVICE_SORT_KEYS, SERVICE_DEFAULT_SORT);

  const { currency } = useTenantCurrency();
  const services = useResource(
    useCallback(() => listServices({ status: filter.status }), [filter.status]),
    [filter.status],
  );

  // Resolve bucket ids → names for the list (a free service shows "Free"). A failure
  // degrades to `—` (DNKADM-0303, matching the service-detail Settings panel — a raw
  // bucket id tells an operator nothing); it never blocks the list.
  const buckets = useResource(
    useCallback(() => listCreditBuckets({ status: 'all' }), []),
    [],
  );
  const bucketNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of (buckets.data ?? []) as CreditBucket[]) {
      map.set(b.id, b.name.length > 0 ? b.name : b.id);
    }
    return map;
  }, [buckets.data]);

  // The full active categories taxonomy — the set of sections for the grouped view,
  // so an empty category still shows (count 0). ONE call; never per-service. A failure
  // degrades to grouping by only the categories referenced on the rows.
  const categories = useResource(
    useCallback(() => listServiceCategories({ status: 'active' }), []),
    [],
  );

  // Dialog state: `null` = closed; `{ service: null }` = add; `{ service }` = edit.
  const [dialog, setDialog] = useState<{ service: V2Service | null } | null>(null);

  // Guest-booking preview (DNKADM-0283). The affordance renders only when the
  // member origin is recoverable from this console's host (`admin.<memberHost>`,
  // env.memberOrigin) — never a guessed or configured host.
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewAvailable = guestBookingPreviewUrl() !== null;

  const refetch = services.refetch;

  const handleDeactivate = useCallback(
    async (service: V2Service) => {
      try {
        await deactivateService(service.id);
        refetch();
        showToast('Service turned off', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t turn the service off.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleReactivate = useCallback(
    async (service: V2Service) => {
      try {
        await reactivateService(service.id);
        refetch();
        showToast('Service turned on', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t turn the service on.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handlePublish = useCallback(
    async (service: V2Service) => {
      try {
        await publishService(service.id);
        refetch();
        showToast('Service turned on', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t turn the service on.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleCopy = useCallback(
    async (service: V2Service) => {
      try {
        await copyService(service.id);
        refetch();
        showToast('Service duplicated. The copy starts as a Draft.', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t duplicate the service.', 'error');
      }
    },
    [refetch, showToast],
  );

  const bucketLabel = useCallback(
    (service: V2Service): string => {
      if (service.creditBucketId === null) return 'Free';
      // Unresolvable (failed/absent bucket read, or an id the read doesn't
      // know) renders `—`, never the raw id — the ServiceSettingsPanel
      // convention (DNKADM-0303). Not an error state.
      return bucketNameById.get(service.creditBucketId) ?? '—';
    },
    [bucketNameById],
  );

  // The rows in view: searched, then sorted. Grouping keeps this order inside each
  // section, so the sort applies within categories (DNKADM-0430).
  const visibleServices = useMemo(
    () =>
      sortRows(
        (services.data ?? []).filter((service) => serviceMatches(service, search)),
        (service) => serviceSortValue(service, sort.key, bucketLabel),
        sort.dir,
        (service) => service.id,
      ),
    [services.data, search, sort.key, sort.dir, bucketLabel],
  );
  const onSort = useCallback(
    (key: ServiceSortKey) => setUrl(toSortParams(toggleSort(sort, key), SERVICE_DEFAULT_SORT)),
    [setUrl, sort],
  );

  // Build the grouped layout from the rows + the categories list (null ⇒ flat).
  const categoriesFieldPresent = useMemo(
    () => (services.data ?? []).some((s) => s.categories !== undefined),
    [services.data],
  );
  const grouped = useMemo(
    () => groupByCategory(visibleServices, categories.data ?? [], categoriesFieldPresent),
    [visibleServices, categories.data, categoriesFieldPresent],
  );

  // The sections in render order: the categories-list sections, then a trailing
  // "Uncategorized" pseudo-section (last) when any service has no category. This is
  // the list the expandable-table renders and the source of the "first section".
  const renderSections = useMemo<CategorySection[]>(() => {
    if (grouped === null) return [];
    // While searching, a section with no match is hidden (an empty category still shows
    // otherwise).
    const list = grouped.sections.filter((section) => !searching || section.services.length > 0);
    if (grouped.uncategorized.length > 0) {
      list.push({ id: UNCATEGORIZED_ID, name: 'Uncategorized', services: grouped.uncategorized });
    }
    return list;
  }, [grouped, searching]);

  // Expanded-section state. The FIRST section (render order; Uncategorized last) opens
  // by default; `expanded` holds explicit per-section overrides, so a section is open
  // when `expanded[id] ?? isFirst`. Toggling any section is independent. Seeding by
  // index needs no effect — it stays correct as the async-loaded sections arrive.
  // While a search is active every section opens by default, so a match is never hidden
  // in a collapsed section (DNKADM-0430).
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const isOpen = useCallback(
    (id: string, index: number) => expanded[id] ?? (searching || index === 0),
    [expanded, searching],
  );
  const toggleSection = useCallback(
    (id: string, index: number) =>
      setExpanded((prev) => ({ ...prev, [id]: !(prev[id] ?? (searching || index === 0)) })),
    [searching],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" component="h1">
            Services
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            The services members can book. Each one says what members pay with, who
            picks the coach and the bay, and has one or more lengths and prices. Open a
            service to change its basics, where and who, booking rules, and prices.
          </Typography>
        </Box>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
          {previewAvailable && (
            <Button
              variant="outlined"
              startIcon={<Icon name="visibility" />}
              onClick={() => setPreviewOpen(true)}
            >
              Preview guest booking
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Icon name="add" />}
            onClick={() => setDialog({ service: null })}
          >
            Add service
          </Button>
        </Stack>
      </Stack>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search services',
          disabled: services.status !== 'ready',
        }}
        filters={
          <ActiveFilter
            value={filter.value}
            onChange={filter.setValue}
            aria-label="Filter services by status"
          />
        }
        onClear={() => setUrl({ q: null, status: null })}
        canClear={search.length > 0 || filter.value !== 'active'}
      />

      {services.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={220}
          role="status"
          aria-label="Loading services"
          sx={{ borderRadius: 3 }}
        />
      )}

      {services.status === 'error' && (
        <ScreenError
          error={services.error}
          what="the services"
          onRetry={services.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {services.status === 'ready' && services.data && services.data.length === 0 && (
        // First-run empty state (§8) with a strong create CTA.
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: t.lineStrong,
            borderRadius: 3,
          }}
        >
          <Icon name="design_services" sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {filter.value === 'active' ? 'No services yet' : 'No services match this filter'}
          </Typography>
          {filter.value === 'active' && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5, color: 'text.secondary' }}>
                Add your first service, choose who picks the coach and the bay, then
                open it to add locations and lengths.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon name="add" />}
                onClick={() => setDialog({ service: null })}
              >
                Add service
              </Button>
            </>
          )}
        </Paper>
      )}

      {services.status === 'ready' &&
        services.data &&
        services.data.length > 0 &&
        // Flat fallback: the field is absent on every row (the backend hasn't shipped
        // it yet) — render today's single table, unchanged.
        grouped === null && (
          <ServicesTable
            services={visibleServices}
            currency={currency}
            bucketLabel={bucketLabel}
            sort={sort}
            onSort={onSort}
            ariaLabel="Services"
            onPublish={(s) => void handlePublish(s)}
            onCopy={(s) => void handleCopy(s)}
            onDeactivate={(s) => void handleDeactivate(s)}
            onReactivate={(s) => void handleReactivate(s)}
          />
        )}

      {services.status === 'ready' &&
        services.data &&
        services.data.length > 0 &&
        grouped !== null &&
        // A search with no match keeps the grouped table (its head only).
        (renderSections.length > 0 || searching) && (
          // Grouped view (DNKADM-0089): ONE table — a single head (the 9 columns) at the
          // top, then per category a full-width `colSpan` grouping row (a chevron toggle +
          // name + count, the Variations-panel chevron idiom) and, when that group is
          // open, its service rows rendered into the SAME shared columns (conditional
          // render keeps the column grid intact — collapsed groups' rows stay out of the
          // DOM). The first section opens by default.
          <TableContainer>
            <Table size="small" aria-label="Services by category">
              <ServicesTableHead sort={sort} onSort={onSort} />
              <TableBody>
                {renderSections.map((section, index) => {
                  const open = isOpen(section.id, index);
                  return (
                    <Fragment key={section.id}>
                      {/* Grouping row: a subtle brand tint (the mode-aware surfaceA — the
                          light-only GREEN_TINTS const painted a light band in dark mode,
                          DNKADM-0293) + a touch more vertical padding + an uppercase section
                          label make this read as a structural divider, distinct from the
                          service data rows (DNKADM-0090), which stay on the default background. */}
                      <TableRow sx={{ bgcolor: maps.greenTints.surfaceA }}>
                        <TableCell
                          colSpan={SERVICE_COLUMN_COUNT}
                          sx={{ py: 1.5, borderTop: '1px solid', borderTopColor: 'divider' }}
                        >
                          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                            <IconAction
                              size="small"
                              label={
                                open ? `Collapse ${section.name}` : `Expand ${section.name}`
                              }
                              onClick={() => toggleSection(section.id, index)}
                            >
                              <Icon name={open ? 'expand_less' : 'expand_more'} />
                            </IconAction>
                            {/* The explicit space keeps the readable "<name> · <count>".
                                The name renders as an uppercase, letter-spaced section label;
                                the count stays muted (text.secondary, normal weight/casing). */}
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                              }}
                            >
                              {section.name}{' '}
                              <Box
                                component="span"
                                sx={{
                                  fontWeight: 400,
                                  textTransform: 'none',
                                  letterSpacing: 'normal',
                                  color: 'text.secondary',
                                }}
                              >
                                · {section.services.length}
                              </Box>
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {open &&
                        (section.services.length > 0 ? (
                          section.services.map((service) => (
                            <ServiceRow
                              key={service.id}
                              service={service}
                              currency={currency}
                              bucketLabel={bucketLabel}
                              onPublish={(s) => void handlePublish(s)}
                              onCopy={(s) => void handleCopy(s)}
                              onDeactivate={(s) => void handleDeactivate(s)}
                              onReactivate={(s) => void handleReactivate(s)}
                            />
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={SERVICE_COLUMN_COUNT}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                No services in this category yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      {services.status === 'ready' &&
        services.data &&
        services.data.length > 0 &&
        visibleServices.length === 0 && <ListNoMatch noun="services" query={search} />}

      <ServiceDialog
        open={dialog !== null}
        service={dialog?.service ?? null}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
        }}
        showToast={showToast}
      />

      <GuestBookingPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </Box>
  );
}
